import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { InitialResolver } from "../lib/use.middlewares";
import { Category } from "../lib/path-resolver-middlewares/category";
import ListingResolver from "../lib/path-resolver-middlewares/listing";
import { City } from "../lib/path-resolver-middlewares/city";
import { Listing } from "../components/Listing/ListingCard";
import { Direction } from "../lib/path-resolver-middlewares/direction";
import { District } from "../lib/path-resolver-middlewares/district";
import BreadCrumb, {
  BreadCrumbHolder,
} from "../components/new-resolver/breadCrumb";
import useFindListings from "../components/new-resolver/useFindListings";
import css from "./.module.scss";
import { ListingHolder } from "@/containers/UserListings/UserListings";
import {
  CategoryList,
  CountersButtons,
  CountersButtonsHolder,
} from "@/components/new-resolver/CountersButtons";
import { Filters } from "@/components/filters/filters";
import { Filter } from "../lib/path-resolver-middlewares/filters";
import ListingScreen, {
  ListingScreenHolder,
} from "../components/Listing/ListingScreen";
import { generateUrl, usePath } from "../contexts/path/path";
import Title, { getTitle } from "../components/Title";
import AppLayout from "../containers/layouts/AppLayout";
import { SpecialList } from "../components/Listing/Special";
import DistrictHeader from "../components/Listing/DistrictLevel/districtLevelHeader";
import { AppLink } from "../components/new-resolver/app.ink";
import { trackListingsList } from "@/lib/tracking/trackNavigationEvents";
import {
  listingToEECItemObject,
  trackSelectItem,
  trackViewItemList,
  trackViewProperties,
} from "@/lib/tracking/trackEECEvents";
import { AllQuickFiltersAsLinks } from "@/components/filters/quick/filters";
import dynamic from "next/dynamic";
import Skeleton from "@/components/skeleton";
import { SSRError } from "@/components/error/ssr";
import { trackSelectDMRItem } from "@/lib/tracking/trackDMREvents";
import { trackMapAuctionListingClicked } from "@/lib/tracking/trackAuctionListingEvents";

const AppliedFilters = dynamic(() => import("@/components/filters/applied"), {
  ssr: false,
});
const arr = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
];

function ListingsHolder() {
  return arr.map((k) => <ListingHolder key={k} />);
}
const template = "%category %city %direction %district";
export function TitleAsH2() {
  const { category, city, direction, district } = usePath();
  const title = useMemo(() => {
    if (direction)
      return template
        .replace(/%category/g, category?.plural?.concat(" في ") || "")
        .replace(/%city/g, "")
        .replace(/%direction/g, direction?.name || "")
        .replace(/%district/g, district?.name || "");

    return template
      .replace(/%category/g, category?.plural?.concat(" في ") || "")
      .replace(/%city/g, city?.name || "")
      .replace(/%direction/g, direction?.name || "")
      .replace(/%district/g, district?.name || "");
  }, [category, city, direction, district]);
  if (!category || !city) return null;

  return <h2>{title}</h2>;
}

function AppliedFiltersWrapper() {
  const { hasFilters, ssr } = usePath();
  const [t] = useState(hasFilters && ssr);
  return (
    <div style={{ height: t ? "2.5rem" : undefined }}>
      <AppliedFilters />
    </div>
  );
}
export function ListPagination({ total, loading, activeClass }) {
  const path = usePath();
  const { page, updateContext } = path;
  const [count, setCount] = useState(Math.ceil(total / 20) || 0);
  useEffect(() => {
    if (loading) return;
    setCount(Math.ceil(total / 20) || 0);
  }, [total, loading]);
  const pages = useMemo(() => {
    const buttonsToRenderCount = Math.max(count % 5, Math.min(4, count));
    if (count <= page + 2)
      return new Array(buttonsToRenderCount)
        .fill(0)
        .map((_, i) => i + 1 + count - buttonsToRenderCount);
    if (page === 1)
      return new Array(buttonsToRenderCount).fill(0).map((_, i) => i + page);
    return new Array(buttonsToRenderCount)
      .fill(0)
      .map((_, i) => i + (page || 2) - 1);
  }, [page, total, count]);

  const url = useMemo(
    () =>
      generateUrl({
        ...path,
        page: undefined,
        listing: undefined,
      }).split("?")?.[0] + (path.filterAsPath || ""),
    [path],
  );

  if (!count || pages.length === 1) return null;
  return (
    <div className={css.pagination}>
      <button
        onClick={() => {
          if (page < 2) return;
          window.scroll(0, 0);

          if (page === 2) return updateContext({ page: 0 });

          updateContext({ page: page - 1 });
        }}
        type="button"
      >
        «
      </button>
      {pages.map((p) => (
        <AppLink
          onClick={() => {
            if (!p || p === page) return;
            window.scroll(0, 0);
            updateContext({ page: p });
          }}
          key={p}
          path={`${url + (p === 1 ? "" : `/${p}`)}`.replaceAll("//", "/")}
          className={
            (`${p}` === `${page || 1}` && (activeClass || css.active)) ||
            undefined
          }
        >
          <span>{p}</span>
        </AppLink>
      ))}
      <button
        onClick={() => {
          if (page + 1 > count) return;

          window.scroll(0, 0);

          updateContext({ page: page ? page + 1 : 2 });
        }}
        type="button"
      >
        »
      </button>
    </div>
  );
}
export function NoListingsFound() {
  const path = usePath();
  const [filterStringTitle] = useMemo(() => {
    return getTitle(path);
  }, [path]);
  return (
    <div>
      <p>
        لا توجد نتائج
        {path.district || path.city ? " في " : ""}
        {`${path?.district?.name || ""} ${path?.direction?.name || ""}  ${
          path?.city?.name || ""
        }`}
        {" حاليا "}
      </p>
      <p>
        {filterStringTitle}

        <button
          onClick={() => {
            path.updateContext({
              filters: {},
            });
          }}
          type="button"
          style={{ textDecoration: "underline" }}
        >
          اضغط هنا لإعادة ضبط الفلاتر
        </button>
      </p>
    </div>
  );
}
function ListingsScreen({ setFrozenContext }) {
  const openSearchBarRef = useRef();
  const path = usePath();
  const {
    district,
    city,
    direction,
    category,
    page,
    filters,
    categoriesDictionary,
    previousPath,
  } = path;

  const [listings, { sov, loading, total }] = useFindListings({}, () => {
    trackViewItemList({
      itemListName: category?.ga_listing_type
        ? path.itemListName
        : "all_listings",
      listingType: category?.ga_listing_type || path.listingType,
      category:
        category?.ga_property_category ||
        (category || categoriesDictionary["0"])?.en,
      cityName: city?.name_en,
      directionName: direction?.name_en,
      districtName: district?.name_en,
      filters,
      count: total,
      listings: (listings || []).map((listing, index) =>
        listingToEECItemObject(listing, index, categoriesDictionary),
      ),
    });
    trackViewProperties({
      listingType: category?.ga_listing_type || path.listingType,
      viewType: "list",
    });
  });
  const [isExpand, setIsExpand] = useState(true);

  useEffect(() => {
    if (window.collapsed) return;
    setIsExpand(false);
    window.collapsed = true;
  }, []);
  const bottomCountersButtons = createRef();
  const scrollToBottomCounters = useCallback(() => {
    setIsExpand(true);
    if (bottomCountersButtons?.current) {
      bottomCountersButtons.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [bottomCountersButtons]);

  useEffect(() => {
    if (path.listing) return;
    setFrozenContext(path);
  }, [path]);

  useEffect(() => {
    function open() {
      trackListingsList(path, listings, { event: "open_listing_list_filters" });
    }
    document.addEventListener("opensearchbar", open);
    return () => {
      document.removeEventListener("opensearchbar", open);
    };
  }, [path, listings]);

  return (
    <div>
      <Title />

      <CategoryList />

      <div className={css.container}>
        <BreadCrumb />
        <TitleAsH2 />
        {!district && (
          <CountersButtons
            isExpand={isExpand}
            onExpand={scrollToBottomCounters}
          />
        )}
        {district && <DistrictHeader />}
        {!district && <AppliedFiltersWrapper />}
        {city && !district && <SpecialList listings={sov?.listings || []} />}
        <div className={css.list}>
          {loading ? (
            <ListingsHolder />
          ) : (
            listings.map((listing, index) => (
              <Listing
                key={index}
                listing={listing}
                index={index}
                preloadImage={index < 4}
                trackClickEvent={() => {
                  const itemListName = category
                    ? path.itemListName
                    : `${
                        categoriesDictionary[listing.category]?.ga_listing_type
                      }_${
                        categoriesDictionary[listing.category]
                          ?.ga_property_category
                      }`;
                  trackSelectItem({
                    itemListName: itemListName,
                    listingType: category?.ga_listing_type,
                    category: category?.en,
                    cityName: city?.name_en,
                    directionName: direction?.name_en,
                    districtName: district?.name_en,
                    filters,
                    count: total,
                    listings: [listing].map((listing, index) =>
                      listingToEECItemObject(
                        listing,
                        index,
                        categoriesDictionary,
                      ),
                    ),
                  });
                  if (listing.daily_rentable) {
                    trackSelectDMRItem({
                      source: previousPath || encodeURI(document.referrer),
                      listing: listing,
                      filters,
                      categoriesDictionary,
                    });
                  }


                  if(listing.is_auction)
                    trackMapAuctionListingClicked({
                      source: previousPath || decodeURI(document?.referrer),
                      propertyCategory: categoriesDictionary?.[listing?.category]?.ga_property_category,
                      listingType: categoriesDictionary?.[listing?.category]?.ga_listing_type,
                    })
                }}
              />
            ))
          )}
          {district && !page && total <= 10 ? (
            <NearByListings size={20 - (listings?.length || 0)} />
          ) : null}
        </div>

        {!loading && !listings.length ? <NoListingsFound /> : null}
        <ListPagination total={total} loading={loading} />
        {!district && (
          <div ref={bottomCountersButtons}>
            <CountersButtons
              isExpand={isExpand}
              bottom
              onExpand={scrollToBottomCounters}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function MasterPage() {
  const { listing, city, error } = usePath();
  const [frozenContext, setFrozenContext] = useState();
  // useResolvePathForBackAndForward();
  const [loading2, setLoading2] = useState(false);

  useEffect(() => {
    const inpHandler = () => {
      setLoading2(true);
      setTimeout(() => setLoading2(false), 200);
    };
    window.addEventListener("inp", inpHandler);

    return () => window.removeEventListener("inp", inpHandler);
  }, []);

  if (error) return <SSRError error={error} />;
  if (loading2 && listing) return <ListingScreenHolder />;
  if (loading2)
    return (
      <div className={css.list}>
        <CategoryList />
        <div className={css.container}>
          <BreadCrumbHolder tow={true} />
          {city && (
            <Skeleton className={css.title}>
              <Skeleton.Item style={{ width: "10rem", height: "1.5rem" }} />
            </Skeleton>
          )}
          <CountersButtonsHolder />
          <ListingsHolder />
        </div>
      </div>
    );
  if (listing) return <ListingScreen frozenContext={frozenContext} />;
  return <ListingsScreen setFrozenContext={setFrozenContext} />;
}

MasterPage.getInitialProps = InitialResolver(
  /**
   *
   *  Redirect to the listing page if the id is greater than 1000000 and category is عقارات
   *
   *  /عقارات/501455
   * */
  async (ctx) => {
    try {
      const [category, id] = (ctx?.query?.params || []).map(decodeURI);
      if (
        category === "عقارات" &&
        /[0-9]+/.test(id) &&
        parseInt(id) > 1000000
      ) {
        ctx.redirect("/ad/" + id);
      }
    } catch (e) {}
  },
  /* withServerOnly: insures it only run on SSR */

  /* Category.Resolve: Resolves if path=/category/page? */
  Filter.Resolve,
  Category.Trendy,

  Category.Resolve,

  /* City.Resolve: Resolves if path=/category/city/page? */
  City.Resolve,

  /* Direction.Resolve: Resolves if path=/category/city/direction/page? */
  Direction.Resolve,

  /* District.Resolve: Resolves if path=/category/riyadhOrJeddah/direction/district/page?
   * District.Resolve: Resolves if path=/category/city/district/page?
   * */
  District.Resolve,

  /* Listing.Resolve: Resolves if path=/category/riyadhOrJeddah/direction/district/listing
   * Listing.Resolve: Resolves if path=/category/city/district/listing
   * */
  ListingResolver.Resolve,
  async () => {
    throw "Not Found";
  },
);

function NearByListings({ size }) {
  const [listings, { loading }] = useFindListings({
    where: { district_id: undefined },
    size,
    skipUpdateCount: true,
    from: 0,
  });

  const obj = useMemo(() => {
    return listings?.reduce(
      (acc, curr) => {
        if (!acc[curr.district_id]) {
          acc[curr.district_id] = [];
          acc.keys.push(curr.district_id);
        }

        acc[curr.district_id].push(curr);
        return acc;
      },
      { keys: [] },
    );
  }, [listings]);

  return loading ? (
    <ListingsHolder />
  ) : (
    obj.keys.map((key, index) => (
      <React.Fragment key={key}>
        <h2> عروض مطابقة في {obj[key][0]?.district}</h2>
        {obj[key].map((listing, j) => (
          <Listing key={listing.id} listing={listing} index={j + index} />
        ))}
      </React.Fragment>
    ))
  );
}
MasterPage.getLayout = ({ children, ...props }) => (
  <AppLayout {...props}>
    <Filters.Layout>{children}</Filters.Layout>
    <AllQuickFiltersAsLinks />
  </AppLayout>
);
export default MasterPage;
